import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Group, LoadingOverlay, Paper, Stack, Switch, Text, Textarea, Title } from '@mantine/core';
import type { MessagingView } from '@/types/messaging';
import styles from './Messaging.module.sass';
import { EditMessagingModal, Icon } from '@/components';
import { formatPhoneNumber } from '@/helpers';
import { useTabStatusContext } from '@/hooks';
import { useMessaging, useUpdateMessagingMutation } from '@/hooks/organization-programs';
import { TimePicker } from '@/components/TimePicker';
import { BILLING_METHOD_LABELS, BULLETIN_TYPE, DAYS_OF_WEEK_LABELS } from '@/types';

const LAYOUT = {
  ARROW_LEFT_ICON_SIZE: 16,
  LOADING_PAPER_MIN_HEIGHT: 200,
  MIN_ROWS: 3,
};

const Messaging = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const { refreshTabStatus } = useTabStatusContext();
  const { data: messagingData, isLoading, isError } = useMessaging(churchId);
  const updateMessagingMutation = useUpdateMessagingMutation(churchId ?? '');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  if (!churchId) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Church ID not found</Text>
      </Paper>
    );
  }

  const handleEditMessaging = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditMessaging = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveMessaging = async (updatedData: MessagingView) => {
    await updateMessagingMutation.mutateAsync(updatedData);
    setIsEditModalOpen(false);
    refreshTabStatus();
  };

  const isActive = messagingData?.isActive;
  const isBulletinDisabled =
    messagingData?.bulletinType === BULLETIN_TYPE.OTHER || messagingData?.bulletinType === BULLETIN_TYPE.LPI;

  if (isLoading) {
    return (
      <Paper p="xl" radius="md" w="100%" pos="relative" style={{ minHeight: LAYOUT.LOADING_PAPER_MIN_HEIGHT }}>
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (isError || !messagingData) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Failed to load messaging data</Text>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" radius="md" w="100%">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Title order={3}>Messaging</Title>
            <Button
              onClick={handleEditMessaging}
              variant="default"
              size="xs"
              leftSection={<Icon name="IconEdit" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
            >
              Edit
            </Button>
          </Group>

          <Group gap="md" align="center">
            <Group gap="md" align="center" flex="1">
              <Text className={styles.label} size="sm">
                Status:
              </Text>
              <Badge color={messagingData?.isActive ? 'green' : 'red'} variant="light">
                {messagingData?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Group>
          </Group>

          {isActive && (
            <Stack gap="md">
              <Text className={styles.label} size="sm">
                SMS:
              </Text>
              <Group gap="md" align="center">
                <Group gap="md" align="center" flex="1">
                  <Text size="sm" className={styles.labelSmall}>
                    Send/Receive SMS
                  </Text>
                  <Switch checked={messagingData?.sms?.isActive} readOnly />
                </Group>
                {messagingData?.sms?.isActive && (
                  <Group gap="md" align="center" flex="1">
                    <Text size="sm" className={styles.labelSmall}>
                      SMS Number:
                    </Text>
                    <Text size="sm" className={styles.valueSmall}>
                      {formatPhoneNumber(messagingData?.sms?.number || '')}
                    </Text>
                  </Group>
                )}
              </Group>
              {messagingData?.sms?.billingMethod && (
                <Group gap="md" align="center" flex="1">
                  <Text size="sm" className={styles.labelSmall}>
                    Billing Method:
                  </Text>
                  <Text size="sm" className={styles.valueSmall}>
                    {BILLING_METHOD_LABELS[messagingData?.sms?.billingMethod]}
                  </Text>
                </Group>
              )}
            </Stack>
          )}
          {isActive && !isBulletinDisabled && (
            <>
              <Stack gap="md" className={styles.bulletinSection} mt="md">
                <Text className={styles.label} size="sm">
                  Bulletin:
                </Text>
                <Group gap="md" align="center">
                  <Group gap="md" align="center" flex="1">
                    <Text size="sm" className={styles.labelSmall}>
                      Send Weekly Bulletin
                    </Text>
                    <Switch checked={messagingData?.bulletin?.isSend} readOnly />
                  </Group>
                </Group>

                {messagingData?.bulletin?.isSend && messagingData?.bulletin?.time && (
                  <>
                    <Group gap="md">
                      <Group gap="md" align="center" flex="1">
                        <Text size="sm" className={styles.labelSmall}>
                          Bulletin Send Time:
                        </Text>
                        <Text size="sm" className={styles.valueSmall}>
                          {messagingData.bulletin.day && DAYS_OF_WEEK_LABELS[messagingData.bulletin.day]}
                        </Text>
                        <TimePicker
                          variant="unstyled"
                          value={messagingData.bulletin.time}
                          readOnly
                          format="12h"
                          className={styles.timePicker}
                          classNames={{
                            field: styles.timePickerField,
                          }}
                        />
                      </Group>
                    </Group>
                    <Group gap="md" align="center">
                      <Group gap="md" align="center" flex="1">
                        <Text size="sm" className={styles.labelSmall}>
                          Send Bulletin via SMS text message
                        </Text>
                        <Switch checked={messagingData.bulletin.isBulletinSmsEnabled} readOnly />
                      </Group>
                    </Group>
                  </>
                )}
                {messagingData?.comment && (
                  <Stack gap="xs" className={styles.commentsSection}>
                    <Text className={styles.label} size="sm">
                      Comments:
                    </Text>
                    <Textarea
                      value={messagingData.comment || ''}
                      placeholder="Enter internal comments..."
                      minRows={LAYOUT.MIN_ROWS}
                      readOnly
                    />
                  </Stack>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      <EditMessagingModal
        opened={isEditModalOpen}
        messagingData={messagingData}
        onClose={handleCloseEditMessaging}
        onSave={handleSaveMessaging}
      />
    </>
  );
};

export default Messaging;
